import { Box, Drawer, SxProps, Theme } from '@mui/material'
import { DrawerProps } from '@mui/material/Drawer'

import useResizableWidth from './useResizableWidth'

export interface ResizableDrawerProps extends DrawerProps {
  minWidth?: number
  maxWidth?: number
  defaultWidth?: number
  resizeStep?: number
  resizeHandleLabel?: string
}

const HANDLE_HIGHLIGHT = { backgroundColor: 'primary.light', opacity: 0.4 }

const handleSx: SxProps<Theme> = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 6,
  cursor: 'ew-resize',
  zIndex: 1,
  '&:hover': HANDLE_HIGHLIGHT,
  '&:focus-visible': { ...HANDLE_HIGHLIGHT, outline: 'none' }
}

const ResizableDrawer = ({
  minWidth,
  maxWidth,
  defaultWidth,
  resizeStep,
  resizeHandleLabel = 'Resize panel',
  children,
  ...drawerProps
}: ResizableDrawerProps) => {
  const resizable = useResizableWidth({ minWidth, maxWidth, defaultWidth, step: resizeStep })

  const paperSx: SxProps<Theme> = { ...(drawerProps.PaperProps?.sx ?? {}), maxWidth: '100vw' }
  // Width changes every frame during a drag. Applying it via style instead of
  // sx keeps Emotion from serializing and injecting a new Paper class on each
  // change.
  const paperStyle = { ...drawerProps.PaperProps?.style, width: resizable.width }

  return (
    <Drawer
      {...drawerProps}
      PaperProps={{ ...drawerProps.PaperProps, sx: paperSx, style: paperStyle }}
    >
      {/*
        A focusable separator is the ARIA "window splitter" widget, which does
        support aria-valuenow/min/max. jsx-a11y/aria-query only model the static
        (non-focusable) separator, so they false-positive on the value props here.
      */}
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
      <Box
        role="separator"
        aria-label={resizeHandleLabel}
        aria-orientation="vertical"
        aria-valuenow={resizable.width}
        aria-valuetext={`${resizable.width} pixels`}
        aria-valuemin={resizable.minWidth}
        aria-valuemax={resizable.maxWidth}
        tabIndex={0}
        onPointerDown={resizable.handlePointerDown}
        onPointerMove={resizable.handlePointerMove}
        onPointerUp={resizable.handlePointerUp}
        onPointerCancel={resizable.handlePointerCancel}
        onDoubleClick={resizable.resetToDefault}
        onKeyDown={resizable.handleKeyDown}
        sx={handleSx}
      />
      {children}
    </Drawer>
  )
}

export default ResizableDrawer
